import { useRef, useEffect, useState } from 'react';
import GameManager from '../../features/gameplay/managers/gameManager';
import AssetsLoader from '../../features/gameplay/utils/assetsLoader';
import { useApi } from '../../services/api/ApiProvider';
import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';

import attack1 from './states/attack1.json';
import attack2 from './states/attack2.json';
import attack3 from './states/attack3.json';
import trash1 from './states/trash1.json';
import trash2 from './states/trash2.json';
import your_turn from './states/your_turn.json';
import opponents_turn from './states/opponents_turn.json';
import my_draw_phase from './states/my_draw_phase.json';
import play_blocker from './states/play_blocker.json';
import activate_trigger_with_play_card from './states/activate_trigger_with_play_card.json';
import opponent_played_character from './states/opponent_played_character.json';
import heart_damage from './states/heart_damage.json';
import effect_step_started from './states/effect_step_started.json';
import player_played_stage from './states/player_played_stage.json';
import refresh_phase from './states/refresh_phase.json';
import refresh_phase_with_rested_dons from './states/refresh_phase_with_rested_dons.json';
import taking_life_card from './states/taking_life_card.json';
import opponent_attached_don from './states/opponent_attached_don.json';
import use_counter_character from './states/use_counter_character.json';
import reorder_cards from './states/reorder_cards.json';
import opponent_search_deck from './states/opponent_search_deck.json';
import opponent_reveal_cards from './states/opponent_reveal_cards.json';
import select_from_trash from './states/select_from_trash.json';
import force_no_selection from './states/force_no_selection.json';
import croc_anim from './states/croc_anim.json';
import opponent_returned_character_to_hand from './states/opponent_returned_character_to_hand.json';

const animations = [
    attack1,
    attack2,
    attack3,
    trash1,
    trash2,
    your_turn,
    opponents_turn,
    my_draw_phase,
    play_blocker,
    activate_trigger_with_play_card,
    opponent_played_character,
    heart_damage,
    effect_step_started,
    player_played_stage,
    refresh_phase,
    refresh_phase_with_rested_dons,
    taking_life_card,
    opponent_attached_don,
    use_counter_character,
    reorder_cards,
    opponent_search_deck,
    opponent_reveal_cards,
    select_from_trash,
    force_no_selection,
    croc_anim,
    opponent_returned_character_to_hand,
];

class Mutex {
    constructor() {
        this.locked = false;
    }
    
    lock() {
        this.locked = true;
    }

    unlock() {
        this.locked = false;
    }

    isLocked() {
        return this.locked;
    }
}

export default function AttackAnimationPage() {
    const { app } = usePixiApp();
    const pixiContainerRef = useRef(null);
    const gameManagerRef = useRef(null);
    const mutexRef = useRef(null);
    const [selectedAnimationIndex, setSelectedAnimationIndex] = useState(3);

    const api = useApi();

    useEffect(() => {
        if (!app) return;
        
        const initGame = async () => {
            if (app) {
                gameManagerRef.current = new GameManager();
                await gameManagerRef.current.init({ emit: (event, data) => { console.log(event, data); } }, localStorage);

                if (pixiContainerRef.current) {
                    gameManagerRef.current.attachToPixiApp(app);
                    pixiContainerRef.current.prepend(app.canvas);
                }
            }
        }

        const loadDeckAssets = async () => {
            const deckAssets1 = await api.fetchDeckAssets('ST01');
            const deckAssets2 = await api.fetchDeckAssets('ST02');
            const deckAssets3 = await api.fetchDeckAssets('ST03');
            const deckAssets4 = await api.fetchDeckAssets('ST04');

            await AssetsLoader.loadDecks([deckAssets1, deckAssets2, deckAssets3, deckAssets4]);
        }

        const startGame = async () => {
            await initGame();
            await loadDeckAssets();

            gameManagerRef.current.serverGameState = animations[selectedAnimationIndex].before;
            await gameManagerRef.current.refreshScene();
            await gameManagerRef.current.updateGameState(animations[selectedAnimationIndex].before);
            await gameManagerRef.current.startGame();
        }

        startGame();

        return () => {
            gameManagerRef.current.cleanup();
            gameManagerRef.current.detachFromPixiApp(app);
        }
    }, [app]);

    useEffect(() => {
        mutexRef.current = new Mutex();

        return () => {
            mutexRef.current = null;
        }
    }, []);

    const playAnimation = async () => {
        if (mutexRef.current?.isLocked()) return;

        mutexRef.current?.lock();
        gameManagerRef.current.serverGameState = animations[selectedAnimationIndex].before;
        await gameManagerRef.current.refreshScene();
        await gameManagerRef.current.updateGameState(animations[selectedAnimationIndex].before);
        await gameManagerRef.current.updateGameState(animations[selectedAnimationIndex].after);
        mutexRef.current?.unlock();
    }

    const reset = async () => {
        if (mutexRef.current?.isLocked()) return;

        mutexRef.current?.lock();
        gameManagerRef.current.serverGameState = animations[selectedAnimationIndex].before;
        await gameManagerRef.current.refreshScene();
        await gameManagerRef.current.updateGameState(animations[selectedAnimationIndex].before);
        mutexRef.current?.unlock();
    }

    const setAnimation = (index) => {
        setSelectedAnimationIndex(parseInt(index));
    }

    useEffect(() => {
        if (!gameManagerRef.current) return;

        reset();
    }, [selectedAnimationIndex]);

    return (
        <div style={{ width: '100dvw', height: '100dvh', display: 'flex', overflow: 'hidden' }}>
            <div>
                <h1>Attack Animation</h1>
                <button style={{ display: 'block', margin: '10px 0' }} onClick={() => playAnimation()}>play Animation</button>
                <button style={{ display: 'block', margin: '10px 0' }} onClick={() => reset()}>reset</button>
                <select onChange={(e) => setAnimation(e.target.value)} value={selectedAnimationIndex}>
                    {animations.map((animation, index) => (
                        <option key={animation.name} value={index}>{animation.name}</option>
                    ))}
                </select>
            </div>
            <div style={{ flex: 1 }} ref={pixiContainerRef} />
        </div>
    );
}